import { CircleNotch, ArrowClockwise, ArrowsClockwise, CurrencyDollar, Storefront } from '@phosphor-icons/react';

/**
 * ScrapingTab — image & price scraping admin sub-page.
 * All async actions + status state come from the parent so polling can survive tab switches.
 */
export default function ScrapingTab({
  scraping, scrapeStatus, onScrapeAll, onCleanupImages,
  validating, validateStatus, onValidateAllLinks,
  priceScraping, priceStatus, onScrapeRealPrices,
  auScraping, auStatus, onScrapeAuStores,
}) {
  return (
    <div className="space-y-6" data-testid="scraping-tab">
      <div className="border border-[#E4E4E7] bg-white p-6">
        <h3 className="font-heading font-bold text-lg mb-2">Product Data Scraping</h3>
        <p className="text-sm text-[#52525B] mb-6">Scrape real product images from brand websites with Zappos fallback. Multi-strategy: static fetch, headless browser, and retailer search.</p>
        <div className="flex gap-4 flex-wrap">
          <button onClick={onScrapeAll} disabled={scraping}
            className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="scrape-all-btn">
            {scraping ? <CircleNotch size={18} className="animate-spin" /> : <ArrowClockwise size={18} />}
            {scraping ? 'Scraping...' : 'Scrape All Products'}
          </button>
          <button onClick={onCleanupImages}
            className="bg-[#0A0A0A] text-white font-bold uppercase tracking-wide px-6 py-4 hover:bg-[#52525B] transition-colors flex items-center gap-2"
            data-testid="cleanup-images-btn">
            Clean Up Bad Images
          </button>
        </div>
        {scrapeStatus && (
          <div className="mt-4 bg-[#F4F4F5] border border-[#E4E4E7] p-4" data-testid="scrape-status">
            <p className="font-bold capitalize">{scrapeStatus.status}</p>
            <div className="grid grid-cols-4 gap-4 mt-2 text-sm">
              <div><span className="text-[#52525B]">Total:</span> <strong>{scrapeStatus.total}</strong></div>
              <div><span className="text-[#52525B]">Scraped:</span> <strong>{scrapeStatus.scraped}</strong></div>
              <div><span className="text-[#52525B]">Images Found:</span> <strong className="text-[#10B981]">{scrapeStatus.images_found}</strong></div>
              <div><span className="text-[#52525B]">Errors:</span> <strong className="text-[#FF3B30]">{scrapeStatus.errors}</strong></div>
            </div>
            {scrapeStatus.status === 'running' && (
              <div className="mt-3 w-full h-2 bg-[#E4E4E7]">
                <div className="h-full bg-[#FF3B30] transition-all" style={{ width: `${(scrapeStatus.scraped / (scrapeStatus.total || 1)) * 100}%` }} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-[#E4E4E7] bg-white p-6">
        <h3 className="font-heading font-bold text-lg mb-2">Validate & Clean Store Links</h3>
        <p className="text-sm text-[#52525B] mb-6">Checks every store link, removes broken/404 pages and stores that don't carry the product. Pulls real prices directly from each store. This may significantly reduce store listings but ensures every link works.</p>
        <button onClick={onValidateAllLinks} disabled={validating}
          className="bg-[#DC2626] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#B91C1C] transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="validate-links-btn">
          {validating ? <CircleNotch size={18} className="animate-spin" /> : <ArrowsClockwise size={18} />}
          {validating ? 'Validating...' : 'Validate & Clean All Links'}
        </button>
        {validateStatus && (
          <div className="mt-4 bg-[#F4F4F5] border border-[#E4E4E7] p-4" data-testid="validate-status">
            <p className="font-bold capitalize">{validateStatus.status}</p>
            <div className="grid grid-cols-4 gap-4 mt-2 text-sm">
              <div><span className="text-[#52525B]">Shoes:</span> <strong>{validateStatus.processed}/{validateStatus.total}</strong></div>
              <div><span className="text-[#52525B]">Links Kept:</span> <strong className="text-[#10B981]">{validateStatus.kept}</strong></div>
              <div><span className="text-[#52525B]">Links Removed:</span> <strong className="text-[#FF3B30]">{validateStatus.removed}</strong></div>
              <div><span className="text-[#52525B]">Prices Updated:</span> <strong>{validateStatus.prices_updated}</strong></div>
            </div>
            {validateStatus.status === 'running' && (
              <div className="mt-3 w-full h-2 bg-[#E4E4E7]">
                <div className="h-full bg-[#DC2626] transition-all" style={{ width: `${(validateStatus.processed / (validateStatus.total || 1)) * 100}%` }} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-[#E4E4E7] bg-white p-6">
        <h3 className="font-heading font-bold text-lg mb-2">Real Price Scraping</h3>
        <p className="text-sm text-[#52525B] mb-6">Scrape actual retail prices from Zappos and apply accurate currency conversions (USD/AUD/GBP) across all store listings.</p>
        <button onClick={onScrapeRealPrices} disabled={priceScraping}
          className="bg-[#10B981] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#059669] transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="scrape-prices-btn">
          {priceScraping ? <CircleNotch size={18} className="animate-spin" /> : <CurrencyDollar size={18} />}
          {priceScraping ? 'Scraping Prices...' : 'Scrape Real Prices'}
        </button>
        {priceStatus && (
          <div className="mt-4 bg-[#F4F4F5] border border-[#E4E4E7] p-4" data-testid="price-scrape-status">
            <p className="font-bold capitalize">{priceStatus.status}</p>
            <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
              <div><span className="text-[#52525B]">Processed:</span> <strong>{priceStatus.processed}/{priceStatus.total}</strong></div>
              <div><span className="text-[#52525B]">Prices Found:</span> <strong className="text-[#10B981]">{priceStatus.prices_found}</strong></div>
              <div><span className="text-[#52525B]">Store Prices Updated:</span> <strong>{priceStatus.store_prices_updated}</strong></div>
            </div>
            {priceStatus.status === 'running' && (
              <div className="mt-3 w-full h-2 bg-[#E4E4E7]">
                <div className="h-full bg-[#10B981] transition-all" style={{ width: `${(priceStatus.processed / (priceStatus.total || 1)) * 100}%` }} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-[#E4E4E7] bg-white p-6">
        <h3 className="font-heading font-bold text-lg mb-2">AU Store Scraping (Playwright)</h3>
        <p className="text-sm text-[#52525B] mb-6">Scrape JS-rendered AU stores (The Iconic, Platypus, The Athletes Foot, Hype DC) using headless browser. These sites block static HTTP requests.</p>
        <button onClick={onScrapeAuStores} disabled={auScraping}
          className="bg-[#0A0A0A] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#52525B] transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="scrape-au-btn">
          {auScraping ? <CircleNotch size={18} className="animate-spin" /> : <Storefront size={18} />}
          {auScraping ? 'Scraping AU Stores...' : 'Scrape AU JS Stores'}
        </button>
        {auStatus && (
          <div className="mt-4 bg-[#F4F4F5] border border-[#E4E4E7] p-4" data-testid="au-scrape-status">
            <p className="font-bold capitalize">{auStatus.status}</p>
            <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
              <div><span className="text-[#52525B]">Processed:</span> <strong>{auStatus.processed}/{auStatus.total}</strong></div>
              <div><span className="text-[#52525B]">Links Added:</span> <strong className="text-[#10B981]">{auStatus.added}</strong></div>
            </div>
            {auStatus.status === 'running' && (
              <div className="mt-3 w-full h-2 bg-[#E4E4E7]">
                <div className="h-full bg-[#0A0A0A] transition-all" style={{ width: `${(auStatus.processed / (auStatus.total || 1)) * 100}%` }} />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-[#E4E4E7] bg-white p-6">
        <h3 className="font-heading font-bold text-lg mb-4">How Scraping Works</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div className="border border-[#E4E4E7] p-4">
            <p className="font-bold text-[#FF3B30] uppercase text-xs mb-2">Strategy 1: Static Fetch</p>
            <p className="text-[#52525B]">Fast HTTP request to brand sites. Extracts og:image meta tags and JSON-LD structured data without browser rendering.</p>
          </div>
          <div className="border border-[#E4E4E7] p-4">
            <p className="font-bold text-[#FF3B30] uppercase text-xs mb-2">Strategy 2: Headless Browser</p>
            <p className="text-[#52525B]">Playwright Chromium renders JS-heavy pages to find product images from search results with smart scoring.</p>
          </div>
          <div className="border border-[#E4E4E7] p-4">
            <p className="font-bold text-[#FF3B30] uppercase text-xs mb-2">Strategy 3: Zappos Fallback</p>
            <p className="text-[#52525B]">When brand sites block scrapers, falls back to Zappos search which has reliable product images for all major brands.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
